import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Переїзд без втрат»: a parent chooses what goes to the new school — grades of the year,
 * achievements, clubs, attendance in numbers — and the app makes a «шкільний паспорт»: a page to
 * print, a file and a one-time code for the administration of another school on Parta. The
 * passport is a copy frozen at that moment; chats, «Турбота» and teachers' notes never go, nor the
 * photos of «Альбом року» (classmates are on them).
 */
export type PassportSection = 'grades' | 'achievements' | 'clubs' | 'attendance' | 'yearbook';

export const PASSPORT_SECTIONS: readonly PassportSection[] = [
  'grades',
  'achievements',
  'clubs',
  'attendance',
  'yearbook',
];

export interface Passport {
  version: number;
  madeAt: string;
  student: {
    lastName: string;
    firstName: string;
    middleName: string | null;
    birthDate: string | null;
    class: string;
    grade: number;
    school: string;
    year: string;
  };
  sections: PassportSection[];
  grades?: Array<{
    subject: string;
    average: number | null;
    count: number;
    semesters: Array<{ term: number; value: string }>;
    year: string | null;
  }>;
  achievements?: Array<{ title: string; category: string; level: string; date: string }>;
  clubs?: Array<{ title: string; emoji: string | null }>;
  attendance?: { lessons: number; absent: number; excused: number };
  /** from «Альбом року»: the year in numbers and the best works (no photos, no letters) */
  yearbook?: {
    best: Array<{ points: number; title: string; subject: string; graded_on: string }>;
    homework: number;
    quizzes: { count: number; percent: number | null } | null;
    puzzles: { days: number; right: number } | null;
    season: { key: string; role: string | null; parts: number } | null;
  };
}

export interface ProfileTransfer {
  id: string;
  studentId: string;
  createdById: string;
  sections: PassportSection[];
  passport: Passport;
  createdAt: string;
  expiresAt: string;
  claimedAt: string | null;
  revokedAt: string | null;
}

export type TransferStatus = 'waiting' | 'opened' | 'cancelled' | 'expired';

export function transferStatus(
  t: Pick<ProfileTransfer, 'claimedAt' | 'revokedAt' | 'expiresAt'>,
  now = Date.now(),
): TransferStatus {
  if (t.claimedAt) return 'opened';
  if (t.revokedAt) return 'cancelled';
  return Date.parse(t.expiresAt) <= now ? 'expired' : 'waiting';
}

/** «K7Q2-9PXM-AB»: easier to read out and type. */
export const formatTransferCode = (code: string) => code.match(/.{1,4}/g)?.join('-') ?? code;

/** A file name the new school recognises: «Шкільний паспорт — Олійник Кирило.json». */
export const passportFileName = (p: Passport) =>
  `Шкільний паспорт — ${p.student.lastName} ${p.student.firstName}.json`;

export function usePassportPreview(studentId: string, sections: readonly PassportSection[]) {
  const db = useDb();
  return useQuery({
    queryKey: ['passport', 'preview', studentId, [...sections].sort()],
    enabled: studentId !== '' && sections.length > 0,
    queryFn: async () =>
      must(
        await db.rpc('preview_passport', { p_student: studentId, p_sections: [...sections] }),
      ) as unknown as Passport,
  });
}

/** The passports made: of a child (family) or of the whole school (administration). */
export function useProfileTransfers(filter: { studentId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['passport', 'transfers', filter],
    queryFn: async (): Promise<ProfileTransfer[]> => {
      let query = db
        .from('profile_transfers')
        .select(
          'id, student_member_id, created_by_member_id, sections, snapshot, created_at, expires_at, claimed_at, revoked_at',
        )
        .order('created_at', { ascending: false });
      query =
        'studentId' in filter
          ? query.eq('student_member_id', filter.studentId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((r) => ({
        id: r.id,
        studentId: r.student_member_id,
        createdById: r.created_by_member_id,
        sections: r.sections as PassportSection[],
        passport: r.snapshot as unknown as Passport,
        createdAt: r.created_at,
        expiresAt: r.expires_at,
        claimedAt: r.claimed_at,
        revokedAt: r.revoked_at,
      }));
    },
  });
}

export function useTransferActions() {
  const db = useDb();
  const client = useQueryClient();
  const onSettled = () => void client.invalidateQueries({ queryKey: ['passport'] });
  return {
    create: useMutation({
      mutationFn: async (a: { studentId: string; sections: readonly PassportSection[] }) => {
        const row = must(
          await db.rpc('create_profile_transfer', {
            p_student: a.studentId,
            p_sections: [...a.sections],
          }),
        )[0];
        if (!row) throw new Error('no transfer');
        return { id: row.transfer_id, code: row.transfer_code, validUntil: row.valid_until };
      },
      onSettled,
    }),
    revoke: useMutation({
      mutationFn: async (id: string) =>
        must(await db.rpc('revoke_profile_transfer', { p_transfer: id })),
      onSettled,
    }),
    claim: useMutation({
      mutationFn: async (code: string) =>
        must(await db.rpc('claim_profile_transfer', { p_code: code })) as unknown as Passport,
      onSettled,
    }),
  };
}
